import React, { useState } from "react";
import { Box, styled, useTheme } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ContentArea, LiLink } from "../../../common/commonComps";
import { baseApi } from "../../../common/constants";
import { getSid } from "../../../common/utils";
import type { AppContext, Store } from "../../../types";

const BoxBottom = styled(Box)(() => ({
    position: `relative`,
    textAlign: `left`,
    background: `#FFF`,
}));

interface TopImageProps {
    logoPrimaryColor?: string;
    url?: string;
    storeGuid?: string;
}

const TopImage = styled(Box)<TopImageProps>(({ logoPrimaryColor, url, storeGuid }) => ({
    height: 243,
    width: 433,
    borderRadius: 8,
    backgroundColor: `#${logoPrimaryColor ?? `FFFFFF`}`,
    border: logoPrimaryColor ? `none` : `2px solid #eee`,
    backgroundImage: logoPrimaryColor
        ? `url(https://s3.amazonaws.com/retailerimages/whLogo/${storeGuid})`
        : `url(${url})`,
    backgroundRepeat: `no-repeat`,
    backgroundSize: `55%`,
    backgroundPosition: `center`,
}));

const TextLine = styled(Box)(() => ({
    fontSize: `16px`,
    fontWeight: `700`,
    color: `#333`,
    paddingTop: `5px`,
}));

const Wrap = styled(Box)(() => ({
    background: `#FFFFFF`,
    textAlign: `center`,
    height: `100%`,
    boxSizing: `border-box`,
    position: `relative`,
    borderRadius: `8px 8px 0 0`,
    width: 433,
}));

interface PromotedOfferComponentProps {
    logoPrimaryColor?: string;
    cbp: string;
    was: string;
    storeGuid: string;
    url: string;
    src: string;
    name: string;
    description: string;
    aid: string;
    asid: string;
    setStore?: (store: Store | null) => void;
}

const PromotedOfferComponent = ({
    logoPrimaryColor,
    cbp,
    was,
    storeGuid,
    url,
    src,
    name,
    description,
    aid,
    asid,
    setStore,
}: PromotedOfferComponentProps): React.ReactElement => {
    const theme = useTheme();
    const [hide, setHide] = useState<boolean>(false);

    return (
        <Wrap>
            <LiLink
                target="_blank"
                to={`${baseApi}tooffer?aid=${aid}&asid=${asid}&auth=${encodeURIComponent(getSid()!)}`}
            >
                <Wrap>
                    <TopImage {...{ logoPrimaryColor, storeGuid, url }} />
                    <Box
                        onError={() => setHide(true)}
                        sx={{
                            position: `absolute`,
                            top: 0,
                            left: 0,
                            width: `433px`,
                            height: `auto`,
                            display: hide ? `none` : `inline`,
                            borderRadius: `8px`,
                        }}
                        component="img"
                        src={src}
                    />
                    <BoxBottom>
                        <MoreVertIcon
                            onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                setStore!({ aid, asid, cbp, was, name });
                            }}
                            sx={{ position: `absolute`, right: `-2px`, top: `10px`, color: `#999`, fontSize: 20 }}
                        />
                        <TextLine sx={{ paddingTop: `10px` }}>{name}</TextLine>
                        <TextLine sx={{ paddingTop: `5px`, color: `#333`, fontWeight: 400 }}>{description}</TextLine>
                        {cbp && (
                            <TextLine sx={{ color: theme.palette.account.accent, paddingTop: `15px` }}>{cbp}</TextLine>
                        )}
                        {was && (
                            <TextLine sx={{ color: `#666`, fontWeight: 400, fontSize: 14, paddingTop: `5px` }}>
                                was {was}
                            </TextLine>
                        )}
                    </BoxBottom>
                </Wrap>
            </LiLink>
        </Wrap>
    );
};

interface CustomImagePopRowProps {
    context: AppContext;
    setStore?: (store: Store | null) => void;
    [key: string]: unknown;
}

const CustomImagePopRow = (cx: CustomImagePopRowProps): React.ReactElement => {
    const {
        context: { promotedOffers },
        setStore,
    } = cx;

    return (
        <ContentArea
            className={promotedOffers!.length === 3 ? `between` : `left`}
            style={{ overflowX: `auto`, gap: 20 }}
        >
            {promotedOffers!.map((p, i) => (
                <PromotedOfferComponent key={i} {...{ setStore, ...p }} />
            ))}
        </ContentArea>
    );
};

export default CustomImagePopRow;
